import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Timer, Building2, Globe2 } from 'lucide-react';
interface SlideLocalRelevanceProps {
  onContinue: () => void;
}
export function SlideLocalRelevance({
  onContinue
}: SlideLocalRelevanceProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [400, 1000, 1800, 2600].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-695" data-mp="2xrs1w6269e0e">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-696" data-mp="2js0gvld8mxcw">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-697" data-mp="18dn7flj8ekge">
          The Gap
        </p>
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-698" data-mp="1cg9u7811pf6q">
          Relevance is local — and time is the enemy
        </h2>
        <p className="text-xl text-white/60" data-id="element-699" data-mp="m2bwezdip042">
          Markets need content that reflects local realities, priorities and HCP
          needs.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-5xl mx-auto w-full space-y-8" data-id="element-700" data-mp="v2h317avzuh6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center" data-id="element-701" data-mp="qxr5zf4hs9uo">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-7 h-full flex flex-col" data-id="element-702" data-mp="14xsi5qq7j3pg">
            <div className="w-12 h-12 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center mb-5" data-id="element-703" data-mp="19s66j96gcakm">
              <Globe2 className="w-6 h-6" data-id="element-704" data-mp="1iqrw3oaccpuw" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3" data-id="element-705" data-mp="1isbcdevzz0wo">
              Global campaign development
            </h3>
            <p className="text-white/60 leading-relaxed" data-id="element-706" data-mp="lc6o03zp906h">
              Strategically sound, scientifically rigorous — but often too slow,
              too generic and too far removed from what local teams need now.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scaleY: 0.4
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          scaleY: stage >= 2 ? 1 : 0.4
        }} className="flex md:flex-col items-center justify-center gap-3" data-id="element-707" data-mp="u5a6iqp7c37z">
            <div className="hidden md:block w-[3px] h-16 bg-gradient-to-b from-transparent to-rose-400/50 rounded-full" data-id="element-708" data-mp="18zn3g61v90g0" />
            <span className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs font-semibold tracking-wide uppercase whitespace-nowrap" data-id="element-709" data-mp="3at4e7ilamhez">
              The gap
            </span>
            <div className="hidden md:block w-[3px] h-16 bg-gradient-to-t from-transparent to-rose-400/50 rounded-full" data-id="element-710" data-mp="37bqfhkgly8gd" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : 20
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-7 h-full flex flex-col" data-id="element-711" data-mp="32j5lahtshtcb">
            <div className="w-12 h-12 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center mb-5" data-id="element-712" data-mp="3lb7u5706w5c0">
              <MapPin className="w-6 h-6" data-id="element-713" data-mp="3h8lavuhl3sqf" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3" data-id="element-714" data-mp="3hb7gvpotu06j">
              Local market execution
            </h3>
            <p className="text-white/60 leading-relaxed" data-id="element-715" data-mp="25ty0xd4wpq9z">
              Local teams know the language, the channels, the competitive
              reality and the HCP conversation happening this quarter.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl px-7 py-6 flex items-start gap-4" data-id="element-716" data-mp="3oqhsjp9h0h8r">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center" data-id="element-717" data-mp="bcwloku57rpm">
            <Timer className="w-5 h-5" data-id="element-718" data-mp="gcizowhspbzh" />
          </div>
          <p className="text-white/90 text-lg leading-relaxed" data-id="element-719" data-mp="gb7ssnt1zo45">
            This creates a growing gap between global campaign development and
            local market execution — and it widens with every new brand, market
            and campaign cycle.
          </p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-720" data-mp="2n4jrpw4vfgkh"></motion.div>
    </div>;
}